import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

export type FieldSpec = {
  key: string;
  label: string;
  type?: "text" | "multiline" | "json";
  required?: boolean;
};

type Row = Record<string, any>;

export default function CollectionAdmin({
  title,
  basePath,
  fields,
}: {
  title: string;
  basePath: string;
  fields: FieldSpec[];
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    fetch(basePath, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            res.status === 401
              ? "Du er ikke lenger innlogget."
              : "Kunne ikke laste data."
          );
        }
        return res.json();
      })
      .then((data) => setRows(data))
      .catch((err) =>
        setLoadError(err instanceof Error ? err.message : "Kunne ikke laste data.")
      )
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [basePath]);

  const openCreate = () => {
    setEditingRow(null);
    const initial: Record<string, string> = {};
    fields.forEach((f) => (initial[f.key] = ""));
    setFormValues(initial);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: Row) => {
    setEditingRow(row);
    const initial: Record<string, string> = {};
    fields.forEach((f) => {
      const value = row[f.key];
      initial[f.key] =
        f.type === "json"
          ? value
            ? JSON.stringify(value, null, 2)
            : ""
          : value ?? "";
    });
    setFormValues(initial);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSubmit = async () => {
    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = formValues[f.key] ?? "";
      if (f.type === "json") {
        if (raw.trim() === "") {
          payload[f.key] = null;
          continue;
        }
        try {
          payload[f.key] = JSON.parse(raw);
        } catch {
          setFormError(`Feltet "${f.label}" er ikke gyldig JSON.`);
          return;
        }
      } else {
        payload[f.key] = raw;
      }
    }

    const url = editingRow ? `${basePath}/${editingRow.id}` : basePath;
    const method = editingRow ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setFormError(data.error ?? "Lagring feilet.");
      return;
    }

    setDialogOpen(false);
    load();
  };

  const handleDelete = async (row: Row) => {
    if (!window.confirm(`Slette "${row[fields[0].key]}"?`)) {
      return;
    }
    await fetch(`${basePath}/${row.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    load();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">{title}</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          Ny
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {fields.map((f) => (
                <TableCell key={f.key}>{f.label}</TableCell>
              ))}
              <TableCell align="right">Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              rows.map((row) => (
                <TableRow key={row.id}>
                  {fields.map((f) => (
                    <TableCell
                      key={f.key}
                      sx={{
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.type === "json"
                        ? row[f.key]
                          ? JSON.stringify(row[f.key])
                          : "—"
                        : row[f.key] ?? "—"}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(row)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row)} size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingRow ? "Rediger" : "Ny"}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          {formError && <Alert severity="error">{formError}</Alert>}
          {fields.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              value={formValues[f.key] ?? ""}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, [f.key]: e.target.value }))
              }
              multiline={f.type === "multiline" || f.type === "json"}
              minRows={f.type === "json" ? 4 : f.type === "multiline" ? 3 : 1}
              fullWidth
              required={f.required}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Avbryt</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Lagre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
